import React, { useState, useRef } from 'react';
import { ChevronLeft, ChevronRight, Volume2, VolumeX } from 'lucide-react';

const slides = [
  // TITRE
  {
    id: 1, type: 'title',
    title: "Les principaux pédagogues du DEEJE",
    subtitle: "Pédagogues, psychologues et théoriciens de référence",
    image: "https://images.unsplash.com/photo-1763310225537-f7161d5c93e9?w=800"
  },
  // SOMMAIRE
  {
    id: 2, type: 'summary',
    title: "Sommaire",
    points: [
      "Pédagogues et approches éducatives : Montessori, Pikler, Kergomard, Stern, Malaguzzi",
      "Psychologie du développement : Vygotsky, Bruner, Piaget",
      "Approche systémique : Bronfenbrenner",
      "Attachement et clinique : Bowlby, Winnicott",
      "Résilience et prévention : Cyrulnik, Guedeney",
      "Fondements psychanalytiques : Freud",
      "Communication et dynamique : Lewin, Rosenberg, Rogers",
      "Travail social et petite enfance : David, Appell",
      "Observation et analyse éducative : Fontaine"
    ]
  },
  // ═══════════════════════════════════════
  // SECTION 1 : PÉDAGOGUES ET APPROCHES ÉDUCATIVES
  // ═══════════════════════════════════════
  { id: 3, type: 'section', title: "Pédagogues et approches éducatives" },
  // MONTESSORI
  {
    id: 4, type: 'pedagogue',
    name: "Maria Montessori",
    dates: "1870–1952",
    citation: "Aide-moi à faire seul.",
    portrait: "https://customer-assets.emergentagent.com/job_39ee4c57-44ca-4dc9-ba6c-a177c46d7500/artifacts/12b049cp_image.png",
    description: "Maria Montessori est une médecin et pédagogue italienne qui fonde sa démarche sur une observation scientifique rigoureuse de l'enfant. À partir de son travail auprès d'enfants issus de milieux populaires à Rome, elle élabore une pédagogie centrée sur l'autonomie, la liberté encadrée et le respect du développement naturel.",
    description2: "Son approche repose sur une idée fondamentale : l'enfant possède en lui les ressources nécessaires à son propre développement, à condition que l'environnement lui en donne la possibilité.",
    dc: "DC1"
  },
  {
    id: 5, type: 'content',
    title: "1. Le concept d'« esprit absorbant »",
    content: "Montessori décrit l'enfant de 0 à 6 ans comme doté d'un esprit absorbant, c'est-à-dire d'une capacité naturelle à intégrer les informations de son environnement sans effort conscient. Contrairement à l'adulte qui apprend volontairement, le jeune enfant apprend par immersion. Il absorbe les gestes, les attitudes, le langage, les règles sociales.",
    bullets: [
      "L'enfant apprend en observant les adultes",
      "L'organisation spatiale et relationnelle influence directement son développement",
      "Le climat émotionnel impacte sa sécurité intérieure"
    ]
  },
  {
    id: 6, type: 'content',
    title: "2. L'environnement préparé",
    content: "Montessori insiste sur l'importance d'un environnement adapté à la taille, aux capacités et aux besoins de l'enfant. L'objectif est de permettre à l'enfant d'agir sans solliciter en permanence l'intervention de l'adulte. L'environnement devient un médiateur éducatif.",
    bullets: [
      "Du mobilier à hauteur d'enfant",
      "Des espaces structurés et lisibles",
      "Du matériel accessible et ordonné",
      "Des objets esthétiques, simples et fonctionnels",
      "Des supports auto-correctifs permettant l'expérimentation sans dépendance constante à l'adulte"
    ]
  },
  {
    id: 7, type: 'content',
    title: "3. « Aide-moi à faire seul »",
    content: "Cette phrase résume la posture montessorienne. L'adulte n'est ni directif ni passif. Il observe, accompagne et intervient uniquement lorsque cela est nécessaire. L'autonomie est considérée comme un facteur de construction de l'estime de soi.",
    bullets: [
      "Éviter de faire à la place de l'enfant",
      "Soutenir sans entraver",
      "Encourager l'effort",
      "Valoriser l'initiative"
    ]
  },
  {
    id: 8, type: 'content',
    title: "4. Le respect du rythme individuel",
    content: "Montessori refuse les apprentissages uniformisés et les comparaisons normatives. L'adulte adapte ses propositions en fonction de l'observation fine des intérêts et des capacités de l'enfant.",
    bullets: [
      "Chaque enfant possède son propre rythme d'acquisition",
      "Les périodes sensibles : moments favorables à certains apprentissages",
      "Cheminement personnel respecté"
    ]
  },
  {
    id: 9, type: 'content',
    title: "5. La concentration comme moteur du développement",
    content: "Montessori observe que lorsqu'un enfant est engagé dans une activité choisie librement, il développe une concentration profonde. L'adulte doit donc éviter d'interrompre inutilement un enfant concentré.",
    bullets: [
      "La concentration structure la pensée",
      "Elle favorise la régulation émotionnelle",
      "Elle renforce la confiance en soi"
    ]
  },
  {
    id: 10, type: 'content',
    title: "Lien DC1 : Aménagement des espaces et autonomisation",
    content: "L'approche montessorienne éclaire la conception des espaces et répond aux compétences liées à l'environnement éducatif sécurisant.",
    bullets: [
      "Aménagement : zones différenciées, matériel accessible, ambiance apaisée, ordre visuel structurant",
      "Autonomisation : étagères basses, pichets adaptés, porte-manteaux accessibles, toilettes adaptées",
      "Ces aménagements favorisent l'autonomie quotidienne et s'inscrivent pleinement dans la compétence « Favoriser les capacités d'action de l'enfant »"
    ]
  },
  {
    id: 11, type: 'content',
    title: "Lien DC1 : Observation active et respect du développement",
    content: "Montessori fonde toute sa pédagogie sur l'observation. L'observation guide l'intervention.",
    bullets: [
      "Observation active : centres d'intérêt, degré d'autonomie, périodes sensibles, besoins non exprimés verbalement",
      "Respect du développement individuel : refuser la pression normative, valoriser les progrès personnels",
      "Adapter les propositions aux capacités réelles de l'enfant",
      "Compétences visées : reconnaître les potentialités, favoriser le développement global, respecter le rythme de l'enfant"
    ]
  },
  {
    id: 12, type: 'content',
    title: "Mobiliser Montessori dans le Livret 2",
    content: "Je m'appuie sur les travaux de Maria Montessori qui mettent en évidence l'importance d'un environnement préparé et d'une posture d'accompagnement favorisant l'autonomie. Dans ma pratique, j'aménage les espaces de manière à permettre à l'enfant d'agir par lui-même, tout en assurant une présence contenante et sécurisante."
  },
  // PIKLER
  {
    id: 13, type: 'pedagogue',
    name: "Emmi Pikler",
    dates: "1902–1984",
    citation: "Le développement moteur libre favorise la confiance en soi.",
    portrait: "https://customer-assets.emergentagent.com/job_7ea634f4-6c60-43bd-ad87-41da18485b19/artifacts/x5zrvht5_image.png",
    description: "Emmi Pikler est une pédiatre hongroise reconnue pour ses travaux sur le développement précoce du jeune enfant. Après avoir exercé comme médecin de famille, elle fonde en 1946 l'Institut de Loczy à Budapest, une pouponnière destinée à accueillir des enfants séparés de leur famille.",
    description2: "Son objectif est ambitieux : démontrer qu'un enfant peut se développer harmonieusement en collectivité si ses besoins fondamentaux sont respectés avec constance, qualité relationnelle et stabilité. Son approche repose sur quatre piliers fondamentaux : motricité libre, qualité des soins, stabilité affective et observation rigoureuse.",
    dc: "DC1"
  },
  {
    id: 14, type: 'content',
    title: "1. Le concept de motricité libre",
    content: "La motricité libre constitue l'un des apports centraux de Pikler. Elle repose sur l'idée que l'enfant acquiert ses compétences motrices par lui-même, sans être placé dans des positions qu'il ne maîtrise pas encore.",
    bullets: [
      "Ne pas asseoir un enfant qui ne sait pas s'asseoir seul",
      "Ne pas le mettre debout s'il ne se redresse pas de manière autonome",
      "Lui permettre d'explorer librement dans un espace sécurisé",
      "L'enfant développe une meilleure coordination et une confiance corporelle solide",
      "Une sécurité intérieure renforcée et une autonomie durable",
      "La motricité devient un processus d'expérimentation autonome et non un apprentissage dirigé"
    ]
  },
  {
    id: 15, type: 'content',
    title: "2. Les soins individualisés comme temps relationnel privilégié",
    content: "Pour Pikler, les temps de soins (change, repas, habillage) ne sont pas de simples actes techniques. Ils constituent des moments éducatifs et relationnels majeurs.",
    bullets: [
      "Le soin est individualisé, verbalement accompagné, réalisé dans la lenteur et la disponibilité",
      "Fondé sur le respect et la coopération de l'enfant",
      "L'adulte explique ce qu'il fait et attend la participation de l'enfant",
      "L'adulte maintient un contact visuel et respecte le rythme",
      "Ces moments structurent la sécurité affective et renforcent le sentiment d'être reconnu comme sujet"
    ]
  },
  {
    id: 16, type: 'content',
    title: "3. La sécurité affective fondée sur la stabilité des adultes",
    content: "Pikler insiste sur l'importance d'une figure de référence stable. Même en collectivité, l'enfant a besoin d'un adulte repère qui assure une continuité relationnelle.",
    bullets: [
      "La stabilité favorise l'attachement",
      "Elle réduit l'anxiété",
      "Elle soutient la régulation émotionnelle",
      "Elle permet une exploration sécurisée",
      "L'enfant sait à qui il peut se référer. Cette continuité constitue un socle affectif indispensable"
    ]
  },
  {
    id: 17, type: 'content',
    title: "4. L'observation quotidienne comme outil central",
    content: "À Loczy, l'observation systématique des enfants constitue un outil professionnel fondamental. Elle fonde une posture professionnelle réflexive et non intrusive.",
    bullets: [
      "Les professionnels consignent les progrès moteurs, les interactions, les signaux émotionnels et les rythmes individuels",
      "L'observation permet d'ajuster les pratiques",
      "Elle permet de prévenir les troubles",
      "Elle permet de respecter le développement propre à chaque enfant"
    ]
  },
  {
    id: 18, type: 'content',
    title: "Lien DC1 – Relation privilégiée et Soins éducatifs",
    content: "Le système de référence trouve un écho direct dans l'approche piklérienne. La stabilité relationnelle renforce la sécurité affective, facilite la séparation et soutient l'attachement sécurisé.",
    bullets: [
      "Une organisation réfléchie des référents et une continuité dans les soins",
      "Une présence contenante dans la structure d'accueil",
      "Les soins deviennent des temps pédagogiques à part entière",
      "Ils permettent l'apprentissage de la coopération, la construction de l'estime de soi et la structuration du lien adulte-enfant"
    ]
  },
  {
    id: 19, type: 'content',
    title: "Lien DC1 – Prévention précoce et Aménagement sécurisant",
    content: "L'observation fine permet de repérer les retards moteurs, les signes de retrait relationnel et les difficultés d'attachement. La posture piklérienne s'inscrit dans une logique de prévention douce et quotidienne.",
    bullets: [
      "Un sol ferme et sécurisé pour la motricité libre",
      "Des objets simples favorisant l'exploration",
      "Une absence de matériel contraignant (transats, trotteurs)",
      "Une liberté de mouvement dans un cadre contenant",
      "L'environnement soutient l'activité autonome sans surexcitation"
    ]
  },
  {
    id: 20, type: 'content',
    title: "Mobiliser Pikler dans le Livret 2",
    content: "Je m'appuie sur les travaux d'Emmi Pikler qui mettent en avant l'importance de la motricité libre et de la qualité relationnelle dans les soins. Je veille à ne pas placer l'enfant dans des positions qu'il ne maîtrise pas seul et j'accorde une attention particulière à la verbalisation et au respect de son rythme lors des temps de change et de repas.",
    bullets: [
      "Les soins quotidiens constituent, selon l'approche piklérienne, des temps privilégiés de construction du lien",
      "J'inscris ma pratique dans cette perspective en instaurant une relation individualisée et stable"
    ]
  },
  // KERGOMARD
  {
    id: 21, type: 'pedagogue',
    name: "Pauline Kergomard",
    dates: "1838–1925",
    citation: "Le jeu est le travail de l'enfant.",
    portrait: "https://customer-assets.emergentagent.com/job_7ea634f4-6c60-43bd-ad87-41da18485b19/artifacts/do1w6tpz_image.png",
    description: "Pauline Kergomard est une figure fondatrice de l'école maternelle française. Nommée inspectrice générale des écoles maternelles à la fin du XIXe siècle, elle œuvre pour transformer les anciennes « salles d'asile » en véritables lieux d'éducation respectueux du jeune enfant.",
    description2: "À une époque marquée par une vision scolaire rigide et disciplinaire, elle défend une conception humaniste et progressive de l'éducation préscolaire. Son engagement contribue à faire reconnaître l'école maternelle comme un espace d'éveil et non comme une préparation prématurée à l'école élémentaire.",
    dc: "DC2"
  },
  {
    id: 22, type: 'content',
    title: "1. L'école maternelle comme lieu d'éveil",
    content: "Kergomard refuse l'idée d'une instruction académique précoce. Elle considère que l'enfant de moins de six ans n'est pas un élève miniature mais un être en développement global. Cette approche marque une rupture avec une logique d'apprentissage formel centré sur la répétition et la mémorisation.",
    bullets: [
      "Respecter les besoins physiologiques et affectifs",
      "Favoriser le développement sensoriel et moteur",
      "Encourager l'expression spontanée",
      "Permettre l'expérimentation",
      "L'éducation maternelle doit être « maternelle » au sens de bienveillante, protectrice et adaptée à l'âge"
    ]
  },
  {
    id: 23, type: 'content',
    title: "2. Le jeu comme activité centrale",
    content: "Kergomard place le jeu au cœur du développement. Elle considère que le jeu constitue l'activité naturelle de l'enfant. Le jeu n'est pas une récompense après le travail : il est le travail de l'enfant.",
    bullets: [
      "L'exploration du monde",
      "L'expression des émotions",
      "L'apprentissage des règles sociales",
      "Le développement du langage",
      "La construction de l'imaginaire",
      "L'enfant apprend par l'action et l'expérimentation, non par la contrainte"
    ]
  },
  {
    id: 24, type: 'content',
    title: "3. Le refus du dressage scolaire",
    content: "Pauline Kergomard critique les pratiques autoritaires et uniformisées. Elle affirme que l'éducation doit respecter la nature de l'enfant et non chercher à la contraindre. Cette position préfigure les approches pédagogiques modernes centrées sur l'enfant.",
    bullets: [
      "Opposition à la discipline rigide",
      "Refus de l'immobilité forcée",
      "Rejet de la répétition mécanique",
      "Critique de l'obéissance sans compréhension"
    ]
  },
  {
    id: 25, type: 'content',
    title: "Lien DC2 – Place du jeu dans l'action éducative",
    content: "Dans le cadre du DC2, la conception de situations éducatives et ludiques trouve un ancrage théorique dans les travaux de Kergomard. Le jeu structure les apprentissages, les activités doivent être adaptées à l'âge et le plaisir constitue un moteur du développement.",
    bullets: [
      "Concevoir des supports favorisant l'expérimentation",
      "Proposer des situations de manipulation",
      "Stimuler l'imaginaire",
      "Favoriser l'interaction sociale"
    ]
  },
  {
    id: 26, type: 'content',
    title: "Lien DC2 – Situations éducatives adaptées",
    content: "L'approche de Kergomard invite à respecter le rythme de l'enfant, éviter la pression scolaire prématurée et valoriser les initiatives spontanées.",
    bullets: [
      "Des activités ouvertes",
      "Des propositions non contraignantes",
      "Une posture d'accompagnement plutôt que de direction",
      "Adapter les situations éducatives au développement de l'enfant"
    ]
  },
  {
    id: 27, type: 'content',
    title: "Mobiliser Kergomard dans le Livret 2",
    content: "Les travaux de Pauline Kergomard me rappellent que l'école maternelle est avant tout un lieu d'éveil. Dans ma pratique, je veille à concevoir des situations éducatives ludiques respectant le rythme et les besoins du jeune enfant.",
    bullets: [
      "En référence à Pauline Kergomard, je considère le jeu comme l'activité centrale du développement",
      "Je privilégie des propositions favorisant l'expérimentation et l'initiative plutôt qu'un apprentissage formel prématuré"
    ]
  },
  // STERN
  {
    id: 28, type: 'pedagogue',
    name: "Arno Stern",
    dates: "Né en 1924",
    citation: "Peindre est un besoin naturel.",
    portrait: "https://customer-assets.emergentagent.com/job_7ea634f4-6c60-43bd-ad87-41da18485b19/artifacts/3yxt3zdz_image.png",
    description: "Arno Stern est un pédagogue et chercheur franco-allemand, connu pour avoir créé le concept du « Closlieu » et du jeu de peindre. Son travail repose sur l'observation attentive des productions graphiques d'enfants et sur la conviction que l'acte de peindre possède une valeur universelle et fondamentale pour le développement de la personne.",
    description2: "Contrairement à une approche artistique centrée sur l'esthétique ou la performance, Arno Stern s'intéresse au processus créatif lui-même. Il distingue l'expression spontanée de l'enfant de la production destinée à être évaluée, exposée ou jugée.",
    dc: "DC1"
  },
  {
    id: 29, type: 'content',
    title: "1. Le jeu de peindre",
    content: "Le « jeu de peindre » n'est ni un atelier d'art plastique classique ni une activité dirigée. Il s'agit d'un espace-temps protégé dans lequel l'enfant peint librement, sans consigne thématique, sans modèle et sans attente de résultat. Pour Stern, peindre est un besoin naturel, comparable au jeu symbolique ou au mouvement.",
    bullets: [
      "Un espace clos, sécurisant et ritualisé (le Closlieu)",
      "Une absence d'observateurs extérieurs",
      "Une posture spécifique de l'adulte, appelé « le servant »",
      "Un matériel simple et constant",
      "Le jeu de peindre vise l'expression personnelle et non la production artistique"
    ]
  },
  {
    id: 30, type: 'content',
    title: "2. L'absence de jugement",
    content: "Un des principes fondamentaux de Stern est l'interdiction du jugement esthétique. L'absence de commentaire protège l'enfant de la recherche de performance ou d'approbation.",
    bullets: [
      "L'adulte ne félicite pas, ne critique pas, n'interprète pas, ne compare pas",
      "Une liberté d'expression totale",
      "Une diminution de la pression sociale",
      "Une exploration authentique"
    ]
  },
  {
    id: 31, type: 'content',
    title: "3. L'expression libre sans modèle",
    content: "Stern considère que proposer un modèle, un thème imposé ou une consigne orientée limite la créativité spontanée. L'activité n'est pas dirigée vers un apprentissage technique mais vers une expérience intérieure.",
    bullets: [
      "L'enfant explore les formes, les couleurs, les rythmes",
      "Il reproduit des figures universelles que Stern appelle « Formulation »",
      "Il développe une expression indépendante des codes culturels"
    ]
  },
  {
    id: 32, type: 'content',
    title: "4. Le respect du processus plutôt que du résultat",
    content: "Dans la perspective de Stern, ce qui compte n'est pas la production finale, mais le cheminement créatif. L'œuvre n'a pas vocation à être exposée ou évaluée. Elle appartient à l'enfant.",
    bullets: [
      "La concentration",
      "L'engagement corporel",
      "L'expression émotionnelle",
      "La construction identitaire"
    ]
  },
  {
    id: 33, type: 'content',
    title: "Lien DC1 – Créativité et Expression artistique",
    content: "L'approche de Stern soutient la compétence « Concevoir les conditions favorisant la créativité ». L'expression artistique est envisagée comme un moyen d'élaboration psychique.",
    bullets: [
      "Proposer du matériel varié et accessible, ne pas imposer de thème, respecter les productions",
      "La créativité devient une exploration personnelle et non une performance",
      "L'expression artistique participe à la régulation émotionnelle, l'affirmation de soi et la symbolisation"
    ]
  },
  {
    id: 34, type: 'content',
    title: "Lien DC1 – Non-interventionnisme éducatif",
    content: "La posture de l'adulte est centrale. Le professionnel soutient sans contrôler. Il garantit le cadre sans intervenir dans le contenu.",
    bullets: [
      "La discrétion",
      "La disponibilité",
      "L'absence de direction",
      "Le respect du rythme"
    ]
  },
  {
    id: 35, type: 'content',
    title: "Mobiliser Arno Stern dans le Livret 2",
    content: "Je m'appuie sur les travaux d'Arno Stern qui mettent en évidence l'importance d'un espace d'expression libre, dépourvu de jugement. Dans mes propositions artistiques, je veille à privilégier le processus créatif plutôt que le résultat attendu.",
    bullets: [
      "Conformément à l'approche d'Arno Stern, je propose des temps d'expression picturale sans modèle ni consigne directive",
      "L'objectif est de favoriser la créativité et la confiance en soi"
    ]
  },
  // MALAGUZZI
  {
    id: 36, type: 'pedagogue',
    name: "Loris Malaguzzi",
    dates: "1920–1994",
    citation: "L'enfant possède cent langages pour s'exprimer.",
    portrait: "https://customer-assets.emergentagent.com/job_7ea634f4-6c60-43bd-ad87-41da18485b19/artifacts/w2th2icq_image.png",
    description: "Loris Malaguzzi est un pédagogue italien à l'origine de l'approche éducative de Reggio Emilia, développée après la Seconde Guerre mondiale dans la ville du même nom. Cette approche repose sur une vision profondément respectueuse et compétente du jeune enfant.",
    description2: "Pour Malaguzzi, l'enfant est un sujet actif, curieux, doté d'un potentiel créatif immense. L'éducation ne consiste pas à transmettre un savoir préétabli, mais à accompagner un processus de recherche et de co-construction.",
    dc: "DC2"
  },
  {
    id: 37, type: 'content',
    title: "1. Le concept des « 100 langages de l'enfant »",
    content: "Malaguzzi affirme que l'enfant possède « cent langages » pour s'exprimer : langage corporel, verbal, graphique, musical, symbolique, scientifique, relationnel. L'enfant pense à travers le corps, l'image, le mouvement et l'expérimentation.",
    bullets: [
      "L'enfant ne s'exprime pas uniquement par la parole",
      "Il construit sa compréhension du monde par des modes multiples d'expression",
      "Des ateliers artistiques, explorations sensorielles, projets de recherche",
      "Des supports variés : argile, lumière, matériaux naturels",
      "Le rôle de l'adulte est d'accompagner et de reconnaître ces formes d'expression"
    ]
  },
  {
    id: 38, type: 'content',
    title: "2. L'environnement comme « troisième éducateur »",
    content: "Selon Malaguzzi, l'environnement éducatif joue un rôle actif dans le développement de l'enfant. Après la famille (premier éducateur) et les professionnels (deuxième éducateur), l'espace devient le troisième éducateur. Il ne s'agit pas d'un simple décor mais d'un outil pédagogique.",
    bullets: [
      "Une esthétique soignée",
      "Des matériaux naturels",
      "Une organisation claire et lisible",
      "Des espaces ouverts favorisant l'exploration",
      "Une mise en valeur des productions des enfants",
      "L'espace invite à l'expérimentation et soutient l'autonomie"
    ]
  },
  {
    id: 39, type: 'content',
    title: "3. La documentation pédagogique",
    content: "La documentation constitue un élément central de l'approche Reggio. Elle transforme l'observation en outil d'analyse et de valorisation.",
    bullets: [
      "Photographier les activités",
      "Recueillir les paroles des enfants",
      "Noter les hypothèses formulées",
      "Analyser les processus d'apprentissage",
      "Rendre visible le cheminement de l'enfant",
      "Soutenir la réflexion de l'équipe et impliquer les familles"
    ]
  },
  {
    id: 40, type: 'content',
    title: "Lien DC2 – Projet éducatif et Documentation",
    content: "L'approche de Malaguzzi soutient une pédagogie de projet. Les projets émergent des intérêts des enfants, de leurs questionnements et de leurs explorations spontanées. Le projet devient un processus vivant et évolutif.",
    bullets: [
      "L'adulte accompagne, reformule et enrichit",
      "La documentation pédagogique permet d'objectiver les progrès et d'analyser les interactions",
      "Elle soutient la conception et l'évaluation des actions éducatives"
    ]
  },
  {
    id: 41, type: 'content',
    title: "Lien DC2 – Aménagement des espaces",
    content: "Penser l'environnement comme troisième éducateur implique une réflexion sur la lumière, l'utilisation de matériaux ouverts, des espaces modulables et une circulation fluide. L'aménagement devient partie intégrante du projet pédagogique.",
    bullets: [
      "L'aménagement soutient l'autonomie, la coopération et l'exploration"
    ]
  },
  {
    id: 42, type: 'content',
    title: "Mobiliser Malaguzzi dans le Livret 2",
    content: "Je m'appuie sur l'approche de Loris Malaguzzi qui considère que l'enfant dispose de « cent langages » pour s'exprimer. Je veille à proposer des supports variés favorisant l'expression corporelle, artistique et symbolique.",
    bullets: [
      "Conformément à la conception de l'environnement comme « troisième éducateur », j'ai repensé l'aménagement de l'espace afin qu'il favorise l'exploration autonome et la coopération",
      "J'utilise la documentation pédagogique pour analyser les processus d'apprentissage et ajuster mes propositions éducatives"
    ]
  },
  // ═══════════════════════════════════════
  // SECTION 2 : PSYCHOLOGIE DU DÉVELOPPEMENT
  // ═══════════════════════════════════════
  { id: 43, type: 'section', title: "Psychologie du développement" },
  // VYGOTSKY
  {
    id: 44, type: 'pedagogue',
    name: "Lev Vygotsky",
    dates: "1896–1934",
    citation: "Ce que l'enfant peut faire aujourd'hui avec de l'aide, il pourra le faire seul demain.",
    portrait: "https://customer-assets.emergentagent.com/job_7ea634f4-6c60-43bd-ad87-41da18485b19/artifacts/y8nbzopt_image.png",
    description: "Lev Vygotsky est un psychologue russe dont les travaux ont profondément renouvelé la compréhension du développement de l'enfant. Contrairement à une approche strictement maturative, il considère que le développement cognitif est indissociable du contexte social et culturel.",
    description2: "Pour Vygotsky, l'enfant se développe à travers les interactions avec les autres. L'apprentissage précède et stimule le développement. Le rôle de l'adulte est donc central dans l'acquisition des compétences.",
    dc: "DC1"
  },
  {
    id: 45, type: 'content',
    title: "1. La Zone Prochaine de Développement (ZPD)",
    content: "La ZPD désigne l'écart entre ce que l'enfant peut faire seul et ce qu'il peut accomplir avec l'aide d'un adulte ou d'un pair plus compétent. Elle correspond à la zone d'apprentissage optimale.",
    bullets: [
      "Si l'activité est trop simple, elle n'apporte pas de progression",
      "Si elle est trop complexe, elle génère frustration et échec",
      "Le professionnel doit situer l'enfant dans cette zone intermédiaire",
      "L'accompagnement doit être ajusté aux compétences émergentes"
    ]
  },
  {
    id: 46, type: 'content',
    title: "2. L'importance de l'interaction sociale",
    content: "Pour Vygotsky, le développement cognitif est fondamentalement social. L'enfant apprend d'abord avec les autres avant d'intérioriser les compétences. L'adulte joue un rôle de médiateur. Les pairs ont également un rôle important.",
    bullets: [
      "Les interactions favorisent le langage",
      "Elles structurent la pensée",
      "Elles permettent l'appropriation des règles sociales",
      "Elles stimulent les capacités de raisonnement"
    ]
  },
  {
    id: 47, type: 'content',
    title: "3. L'apprentissage médiatisé",
    content: "Vygotsky introduit la notion de médiation. L'adulte ou l'environnement servent d'intermédiaires entre l'enfant et la tâche à accomplir. Progressivement, l'enfant intériorise ces médiations et devient autonome.",
    bullets: [
      "Médiation verbale : explication, reformulation, questionnement",
      "Médiation gestuelle : démonstration",
      "Médiation matérielle : outils adaptés",
      "Médiation symbolique : langage, signes culturels"
    ]
  },
  {
    id: 48, type: 'content',
    title: "Lien DC1 – L'étayage et l'accompagnement individualisé",
    content: "Le concept d'étayage correspond à l'accompagnement progressif de l'enfant dans sa ZPD. Chaque enfant possède sa propre Zone Prochaine de Développement.",
    bullets: [
      "Soutenir sans faire à la place",
      "Ajuster l'aide et encourager",
      "Retirer progressivement le soutien",
      "Observer les compétences actuelles et identifier les besoins",
      "Proposer un défi accessible et ajuster l'intervention"
    ]
  },
  {
    id: 49, type: 'content',
    title: "Exemple concret et Mobiliser Vygotsky dans le Livret 2",
    content: "Exemple : Un enfant souhaite enfiler son manteau mais rencontre des difficultés. Plutôt que de le faire à sa place, le professionnel montre le geste, encourage, aide partiellement, puis retire progressivement son aide. L'enfant franchit ainsi une étape située dans sa ZPD.",
    bullets: [
      "En m'appuyant sur le concept de ZPD de Vygotsky, j'ajuste mes interventions afin de proposer des situations adaptées aux compétences émergentes de l'enfant",
      "Je mets en œuvre un étayage progressif, permettant à l'enfant de gagner en autonomie tout en bénéficiant d'un accompagnement sécurisant"
    ]
  },
  // BRUNER
  {
    id: 50, type: 'pedagogue',
    name: "Jerome Bruner",
    dates: "1915–2016",
    citation: "L'apprentissage est un processus actif de construction du sens.",
    portrait: "https://customer-assets.emergentagent.com/job_7ea634f4-6c60-43bd-ad87-41da18485b19/artifacts/96ushbu5_image.png",
    description: "Jerome Bruner est un psychologue américain majeur du courant constructiviste. Influencé par les travaux de Lev Vygotsky, il développe une conception interactive et culturelle de l'apprentissage.",
    description2: "Pour Bruner, l'enfant apprend en interaction avec l'adulte, à travers un processus d'accompagnement progressif. Le développement ne se réduit pas à une maturation biologique : il s'appuie sur la médiation et le dialogue. Ses travaux éclairent directement les pratiques éducatives en petite enfance.",
    dc: "DC1"
  },
  {
    id: 51, type: 'content',
    title: "1. Le concept d'étayage",
    content: "Bruner formalise le concept d'étayage (scaffolding), inspiré de la ZPD de Vygotsky. L'étayage désigne l'aide ajustée qu'un adulte apporte à un enfant pour lui permettre de réussir une tâche qu'il ne pourrait accomplir seul.",
    bullets: [
      "La simplification de la tâche",
      "La focalisation sur l'objectif",
      "La valorisation des réussites partielles",
      "L'encouragement",
      "Le retrait progressif de l'aide",
      "L'objectif est de permettre à l'enfant de s'approprier progressivement la compétence jusqu'à l'autonomie complète"
    ]
  },
  {
    id: 52, type: 'content',
    title: "2. L'apprentissage par découverte",
    content: "Bruner valorise l'apprentissage actif. L'enfant ne doit pas être uniquement destinataire d'un savoir transmis, mais acteur de ses découvertes. Le rôle de l'adulte est de guider l'enfant vers la compréhension.",
    bullets: [
      "L'exploration",
      "L'expérimentation",
      "Le questionnement",
      "La résolution de problèmes simples",
      "Cette posture favorise la curiosité, la motivation et la confiance en soi"
    ]
  },
  {
    id: 53, type: 'content',
    title: "3. L'importance de l'interaction adulte-enfant",
    content: "Pour Bruner, le langage et l'interaction jouent un rôle structurant dans le développement cognitif. Les échanges verbaux soutiennent la construction de la pensée.",
    bullets: [
      "L'adulte donne du sens aux actions de l'enfant",
      "Il structure les situations d'apprentissage",
      "Il introduit progressivement des repères symboliques",
      "L'adulte adapte son discours, reformule, encourage et soutient l'engagement"
    ]
  },
  {
    id: 54, type: 'content',
    title: "Lien DC1 – Accompagnement progressif et Ajustement",
    content: "Le concept d'étayage est particulièrement pertinent dans l'accompagnement quotidien : habillage, apprentissage de la propreté, rangement, activités de manipulation.",
    bullets: [
      "L'adulte observe, intervient partiellement, encourage et retire progressivement son aide",
      "L'accompagnement n'est ni excessif ni absent, il est ajusté",
      "Cette posture favorise l'estime de soi, la motivation et la persévérance"
    ]
  },
  {
    id: 55, type: 'content',
    title: "Exemple concret et Mobiliser Bruner dans le Livret 2",
    content: "Exemple : Un enfant souhaite verser de l'eau dans un verre. Le professionnel propose un petit pichet adapté, montre le geste, laisse essayer, aide si nécessaire, puis retire son soutien dès que possible. Ce processus correspond pleinement à l'étayage décrit par Bruner.",
    bullets: [
      "En référence à Jerome Bruner, je mets en place un étayage progressif afin d'accompagner l'enfant vers l'autonomie, en ajustant mon aide en fonction de ses capacités",
      "Je privilégie l'apprentissage par découverte en laissant l'enfant expérimenter avant d'intervenir"
    ]
  },
  // PIAGET
  {
    id: 56, type: 'pedagogue',
    name: "Jean Piaget",
    dates: "1896–1980",
    citation: "L'enfant ne reçoit pas le savoir, il le construit.",
    portrait: "https://customer-assets.emergentagent.com/job_7ea634f4-6c60-43bd-ad87-41da18485b19/artifacts/h6oskbc5_image.png",
    description: "Jean Piaget est un psychologue et biologiste suisse considéré comme l'un des fondateurs de la psychologie du développement. Il élabore une théorie constructiviste selon laquelle l'enfant construit activement ses connaissances en interagissant avec son environnement.",
    description2: "Pour Piaget, le développement cognitif ne relève ni d'une simple maturation biologique ni d'un apprentissage passif. Il résulte d'un processus dynamique d'adaptation entre l'enfant et son milieu, à travers deux mécanismes fondamentaux : l'assimilation et l'accommodation.",
    dc: "DC1"
  },
  {
    id: 57, type: 'content',
    title: "1. Les stades du développement cognitif",
    content: "Piaget décrit le développement intellectuel comme une succession de stades structurés. Ces stades ne sont pas de simples tranches d'âge. Ils correspondent à des transformations qualitatives de la manière de penser.",
    bullets: [
      "Le stade sensori-moteur (0-2 ans)",
      "Le stade préopératoire (2-7 ans)",
      "Le stade des opérations concrètes (7-11 ans)",
      "Le stade des opérations formelles (adolescence)",
      "On ne peut demander à un enfant de raisonner au-delà de son niveau de structuration cognitive"
    ]
  },
  {
    id: 58, type: 'content',
    title: "2. Le stade sensori-moteur (0-2 ans)",
    content: "Ce stade concerne directement la petite enfance, donc le champ d'intervention de l'EJE. L'intelligence est d'abord pratique. L'enfant apprend en agissant sur les objets. Il ne possède pas encore de pensée symbolique stable.",
    bullets: [
      "L'enfant découvre le monde à travers le mouvement, les sensations, la manipulation",
      "Secouer, taper, jeter, porter à la bouche, faire tomber",
      "Les activités doivent être sensorielles et motrices",
      "La manipulation libre est essentielle",
      "Les expériences répétées structurent l'intelligence"
    ]
  },
  {
    id: 59, type: 'content',
    title: "3. La permanence de l'objet",
    content: "La permanence de l'objet constitue une acquisition majeure du stade sensori-moteur. Elle marque une étape fondamentale vers la pensée symbolique.",
    bullets: [
      "Au début, si un objet disparaît du champ de vision, l'enfant considère qu'il n'existe plus",
      "Progressivement, il comprend que l'objet continue d'exister même s'il ne le voit pas",
      "Une personne absente n'a pas disparu définitivement",
      "Cette construction éclaire l'angoisse de séparation, les jeux de cache-cache, la recherche d'un objet disparu"
    ]
  },
  {
    id: 60, type: 'content',
    title: "Lien DC1 – Adaptation des activités",
    content: "La théorie piagétienne permet d'ajuster les propositions éducatives au niveau réel de développement. Il serait inadapté de proposer des tâches demandant abstraction ou raisonnement symbolique complexe à un enfant de moins de deux ans.",
    bullets: [
      "Jeux de manipulation, activités sensorielles, exploration motrice",
      "Jeux d'apparition/disparition",
      "L'adaptation des activités repose sur une compréhension fine des capacités cognitives"
    ]
  },
  {
    id: 61, type: 'content',
    title: "Lien DC1 – Compréhension du développement et Livret 2",
    content: "Les travaux de Piaget permettent de comprendre les réactions émotionnelles liées à la séparation, d'éviter des attentes irréalistes et de situer les comportements dans une dynamique développementale. Un enfant qui pleure lorsque son parent quitte la pièce n'est pas « capricieux », mais traverse une étape liée à la construction de la permanence de l'objet.",
    bullets: [
      "En m'appuyant sur Piaget, je propose des situations d'exploration concrètes favorisant la manipulation et l'expérimentation, adaptées aux capacités cognitives des jeunes enfants",
      "La compréhension de la permanence de l'objet me permet d'accompagner les temps de séparation en tenant compte du niveau de développement cognitif de l'enfant"
    ]
  },
  // ═══════════════════════════════════════
  // SECTION 3 : APPROCHE SYSTÉMIQUE
  // ═══════════════════════════════════════
  { id: 62, type: 'section', title: "Approche systémique et contextuelle" },
  // BRONFENBRENNER
  {
    id: 63, type: 'pedagogue',
    name: "Urie Bronfenbrenner",
    dates: "1917–2005",
    citation: "Le développement est inscrit dans une série de systèmes concentriques.",
    portrait: "https://customer-assets.emergentagent.com/job_7ea634f4-6c60-43bd-ad87-41da18485b19/artifacts/ja5scfjh_image.png",
    description: "Urie Bronfenbrenner est un psychologue du développement américain, connu pour son modèle écologique du développement humain. Il propose une lecture systémique du développement de l'enfant en considérant que celui-ci évolue au sein de systèmes imbriqués qui interagissent entre eux.",
    description2: "Son approche marque un tournant : l'enfant n'est plus envisagé isolément, mais dans l'ensemble des contextes qui influencent son évolution.",
    dc: "DC4"
  },
  {
    id: 64, type: 'content',
    title: "1. Le modèle écologique – Microsystème et Mésosystème",
    content: "Le microsystème correspond aux environnements proches de l'enfant : ce sont les espaces d'interaction directe. Le mésosystème représente les interactions entre les microsystèmes. La qualité de ces interactions influence directement le développement de l'enfant.",
    bullets: [
      "Microsystème : famille, structure d'accueil, école, groupe de pairs",
      "Mésosystème : relation entre la famille et la crèche, échanges entre parents et professionnels"
    ]
  },
  {
    id: 65, type: 'content',
    title: "1. Le modèle écologique – Exosystème et Macrosystème",
    content: "L'exosystème concerne les environnements qui influencent l'enfant indirectement. Même si l'enfant n'y participe pas directement, ces éléments ont un impact sur sa vie quotidienne. Le macrosystème structure les cadres éducatifs et institutionnels.",
    bullets: [
      "Exosystème : conditions de travail des parents, politiques locales, services sociaux, organisation territoriale",
      "Macrosystème : valeurs culturelles, normes sociales, politiques publiques, représentations de l'enfance"
    ]
  },
  {
    id: 66, type: 'content',
    title: "1. Le modèle écologique – Chronosystème et Vision systémique",
    content: "Le chronosystème intègre la dimension temporelle. Le développement s'inscrit dans le temps et dans les transformations sociétales. Le modèle montre que le développement ne dépend pas uniquement des compétences individuelles.",
    bullets: [
      "Évolutions sociales, changements familiaux, transitions de vie",
      "Les contextes sociaux et institutionnels jouent un rôle majeur",
      "Les interactions entre systèmes influencent la trajectoire de l'enfant",
      "Le professionnel doit adopter une lecture globale et contextualisée"
    ]
  },
  {
    id: 67, type: 'content',
    title: "Lien DC4 – Travail partenarial et Approche territoriale",
    content: "Le modèle écologique éclaire la nécessité du travail en réseau. L'enfant évolue au croisement de plusieurs systèmes. Bronfenbrenner invite à prendre en compte les réalités socio-économiques du territoire.",
    bullets: [
      "Coopérer avec les familles",
      "Travailler avec les partenaires sociaux et médicaux",
      "Articuler les actions avec les institutions locales",
      "Adapter le projet éducatif aux caractéristiques du territoire",
      "L'action éducative s'inscrit dans un contexte social plus large"
    ]
  },
  {
    id: 68, type: 'content',
    title: "Exemple concret et Mobiliser Bronfenbrenner dans le Livret 2",
    content: "Exemple : Dans un quartier prioritaire, les besoins des familles peuvent inclure un soutien à la parentalité, une orientation vers des services sociaux, un accompagnement vers l'accès aux soins. L'EJE mobilise les partenaires locaux. Cette démarche correspond à une approche écologique du développement.",
    bullets: [
      "En m'appuyant sur le modèle écologique de Bronfenbrenner, je considère l'enfant dans l'ensemble des systèmes qui influencent son développement. Mon action s'inscrit dans une dynamique partenariale et territoriale",
      "Le travail en réseau permet d'articuler les différents environnements de l'enfant afin d'assurer une cohérence éducative"
    ]
  },
  // ═══════════════════════════════════════
  // SECTION 4 : ATTACHEMENT ET CLINIQUE
  // ═══════════════════════════════════════
  { id: 69, type: 'section', title: "Attachement et clinique" },
  // BOWLBY
  {
    id: 70, type: 'pedagogue',
    name: "John Bowlby",
    dates: "1907–1990",
    citation: "La base de sécurité permet à l'enfant d'explorer le monde.",
    portrait: "https://customer-assets.emergentagent.com/job_7ea634f4-6c60-43bd-ad87-41da18485b19/artifacts/aaevstyo_image.png",
    description: "John Bowlby est un psychiatre et psychanalyste britannique, fondateur de la théorie de l'attachement. Ses travaux, développés à partir des années 1950, ont profondément transformé la compréhension du lien précoce entre le jeune enfant et ses figures parentales.",
    description2: "À travers ses recherches cliniques et ses observations d'enfants séparés de leurs parents, Bowlby met en évidence que l'attachement constitue un besoin fondamental, au même titre que les besoins physiologiques.",
    dc: "DC1"
  },
  {
    id: 71, type: 'content',
    title: "1. La théorie de l'attachement",
    content: "Selon Bowlby, le jeune enfant est biologiquement programmé pour rechercher la proximité d'une figure protectrice. L'attachement n'est pas une dépendance excessive, mais une base nécessaire au développement harmonieux.",
    bullets: [
      "Un système comportemental inné, activé en cas de stress, fatigue, peur ou inconfort",
      "Destiné à assurer la survie",
      "Le bébé manifeste son attachement par les pleurs, le sourire, l'agrippement, la recherche de contact"
    ]
  },
  {
    id: 72, type: 'content',
    title: "2. La base de sécurité",
    content: "Lorsque l'enfant bénéficie d'une figure stable, disponible et sensible à ses besoins, il développe un attachement sécurisant. L'enfant alterne entre exploration et retour vers la figure d'attachement. Cette dynamique est fondamentale dans les structures d'accueil.",
    bullets: [
      "L'exploration de l'environnement",
      "La régulation émotionnelle",
      "La confiance en soi",
      "L'autonomie progressive"
    ]
  },
  {
    id: 73, type: 'content',
    title: "3. Les styles d'attachement",
    content: "Les recherches ultérieures (notamment celles de Mary Ainsworth) ont permis d'identifier différents styles d'attachement. Ces styles ne sont pas des étiquettes définitives mais des modalités relationnelles adaptatives.",
    bullets: [
      "Attachement sécure : l'enfant explore et se rassure auprès de l'adulte",
      "Attachement insécure évitant : l'enfant minimise l'expression de ses besoins",
      "Attachement insécure ambivalent : grande anxiété et difficulté à se séparer",
      "Attachement désorganisé : comportements contradictoires, souvent liés à des contextes traumatiques"
    ]
  },
  {
    id: 74, type: 'content',
    title: "Lien DC1 – Relation privilégiée et Séparation",
    content: "La théorie de l'attachement soutient directement la mise en place du système de référence en structure. Le professionnel devient une figure d'attachement secondaire. L'angoisse de séparation est normale, témoigne d'un lien affectif construit et doit être accompagnée.",
    bullets: [
      "La disponibilité, la cohérence et la sensibilité aux signaux de l'enfant",
      "Ritualiser les départs, verbaliser les émotions, rassurer sans forcer",
      "Maintenir une présence stable"
    ]
  },
  {
    id: 75, type: 'content',
    title: "Exemple concret et Mobiliser Bowlby dans le Livret 2",
    content: "Exemple : Lors de l'accueil du matin, un enfant pleure au départ de son parent. Le professionnel reconnaît l'émotion, propose un objet transitionnel, maintient une proximité rassurante et laisse le temps nécessaire. Cette posture s'inscrit dans la théorie de l'attachement.",
    bullets: [
      "En référence à Bowlby, je veille à instaurer une relation sécurisante permettant à l'enfant de disposer d'une base de sécurité au sein de la structure",
      "La compréhension des mécanismes d'attachement me permet d'accompagner les séparations avec une posture ajustée, en tenant compte des besoins affectifs de chaque enfant"
    ]
  },
  // WINNICOTT
  {
    id: 76, type: 'pedagogue',
    name: "Donald Winnicott",
    dates: "1896–1971",
    citation: "Le jeu est en lui-même une thérapie.",
    portrait: "https://customer-assets.emergentagent.com/job_7ea634f4-6c60-43bd-ad87-41da18485b19/artifacts/whil6oh7_image.png",
    description: "Donald Winnicott est un pédiatre et psychanalyste britannique dont les travaux ont profondément influencé la compréhension du développement affectif du jeune enfant. Son approche clinique met en lumière l'importance de l'environnement relationnel dans la construction psychique.",
    description2: "Pour Winnicott, le développement harmonieux de l'enfant repose sur la qualité de l'environnement humain qui l'entoure. L'adulte ne « fabrique » pas l'enfant, mais lui offre les conditions nécessaires pour se développer.",
    dc: "DC1"
  },
  {
    id: 77, type: 'content',
    title: "1. La « mère suffisamment bonne »",
    content: "Winnicott introduit ce concept pour désigner un adulte capable de répondre de manière adaptée aux besoins du bébé. Il ne s'agit pas d'une mère parfaite. Ce concept souligne l'importance d'une réponse ajustée et évolutive.",
    bullets: [
      "Un adulte disponible et sensible aux signaux du bébé",
      "Capable d'ajustement progressif",
      "Au début, la réponse doit être quasi immédiate",
      "Progressivement, de petites frustrations permettent à l'enfant de développer sa tolérance et son autonomie"
    ]
  },
  {
    id: 78, type: 'content',
    title: "2. Le holding et le handling",
    content: "Le holding correspond au portage psychique et physique. Le handling renvoie à la manière dont l'enfant est manipulé lors des soins. Ces concepts montrent que les gestes quotidiens participent à la structuration psychique.",
    bullets: [
      "Holding : sécurité physique, continuité, stabilité affective, contenance émotionnelle",
      "Handling : toucher respectueux, contenant et sécurisant lors du change, de la toilette, de l'habillage"
    ]
  },
  {
    id: 79, type: 'content',
    title: "3. L'objet transitionnel",
    content: "L'objet transitionnel (souvent appelé « doudou ») occupe une place particulière dans le développement. Il n'est ni totalement interne ni totalement externe. Il appartient à une zone intermédiaire essentielle. Le respect de cet objet en structure est fondamental.",
    bullets: [
      "Un lien entre le monde intérieur et le monde extérieur",
      "Un support rassurant lors des séparations",
      "Une aide à la régulation émotionnelle"
    ]
  },
  {
    id: 80, type: 'content',
    title: "4. L'aire transitionnelle et le jeu",
    content: "Winnicott considère que le jeu se situe dans une aire transitionnelle, espace psychique intermédiaire entre réalité et imagination. « C'est dans le jeu que l'enfant peut être créatif et utiliser sa personnalité tout entière. » Le jeu n'est pas un simple divertissement : il est structurant.",
    bullets: [
      "L'expression symbolique",
      "L'élaboration des émotions",
      "La construction du self",
      "L'intégration des expériences vécues"
    ]
  },
  {
    id: 81, type: 'content',
    title: "Lien DC1 – Jeu, Sécurité affective et Soins éducatifs",
    content: "Le professionnel aménage des espaces favorables au jeu, respecte l'engagement spontané et évite les interruptions inutiles. Le holding relationnel permet à l'enfant de se sentir protégé, reconnu et contenu. Les soins quotidiens sont des temps privilégiés de holding et de handling.",
    bullets: [
      "La manière dont l'adulte porte, parle, regarde et touche influence la construction du sentiment d'existence",
      "Les soins deviennent des actes éducatifs à part entière"
    ]
  },
  {
    id: 82, type: 'content',
    title: "Exemple concret et Mobiliser Winnicott dans le Livret 2",
    content: "Exemple : Lors du change, le professionnel verbalise chaque geste, maintient le contact visuel, respecte le rythme et invite l'enfant à participer. Ce moment dépasse la simple hygiène : il renforce le lien et la sécurité affective.",
    bullets: [
      "En référence à Winnicott, je veille à proposer un cadre sécurisant favorisant le holding relationnel et permettant à l'enfant d'investir le jeu comme espace d'expression",
      "Je respecte l'objet transitionnel de l'enfant comme support de régulation émotionnelle, particulièrement lors des temps de séparation"
    ]
  },
  // ═══════════════════════════════════════
  // SECTION 5 : RÉSILIENCE ET PRÉVENTION
  // ═══════════════════════════════════════
  { id: 83, type: 'section', title: "Clinique, résilience et prévention" },
  // CYRULNIK
  {
    id: 84, type: 'pedagogue',
    name: "Boris Cyrulnik",
    dates: "Né en 1937",
    citation: "La résilience n'est pas un état, c'est un processus.",
    portrait: "https://customer-assets.emergentagent.com/job_7ea634f4-6c60-43bd-ad87-41da18485b19/artifacts/9jihe7ux_image.png",
    description: "Boris Cyrulnik est neuropsychiatre, psychiatre et éthologue français. Survivant de la Shoah, il a consacré une grande partie de ses travaux à la compréhension des traumatismes et aux mécanismes de reconstruction psychique.",
    description2: "Il a popularisé en France le concept de résilience. Pour Cyrulnik, un traumatisme n'est pas une fatalité. Le devenir d'un enfant dépend en grande partie de la qualité de son environnement relationnel et des rencontres qu'il fera.",
    dc: "DC1"
  },
  {
    id: 85, type: 'content',
    title: "1. La résilience",
    content: "La résilience désigne la capacité d'un individu à se reconstruire après une épreuve traumatique. La résilience est un processus dynamique. Un enfant ayant vécu des difficultés peut évoluer favorablement si son environnement lui offre des appuis solides.",
    bullets: [
      "Donner du sens à l'expérience",
      "Retrouver une capacité d'action",
      "Se réorganiser psychiquement et recréer du lien",
      "Influencée par les relations sécurisantes, le soutien social, les ressources internes et les expériences positives répétées"
    ]
  },
  {
    id: 86, type: 'content',
    title: "2. Le rôle des « tuteurs de résilience »",
    content: "Cyrulnik souligne l'importance des figures significatives dans le parcours de reconstruction. En structure d'accueil, le professionnel peut jouer ce rôle.",
    bullets: [
      "Un tuteur peut être un parent, un professionnel, un enseignant, un éducateur",
      "Cette figure accueille la parole et reconnaît la souffrance",
      "Elle valorise les compétences et offre un cadre sécurisant"
    ]
  },
  {
    id: 87, type: 'content',
    title: "3. La reconstruction après traumatisme",
    content: "Le traumatisme modifie la manière dont l'enfant perçoit le monde. Le jeu et la relation deviennent des supports thérapeutiques indirects.",
    bullets: [
      "Hypervigilance, retrait relationnel, troubles émotionnels, difficultés d'attachement",
      "La reconstruction passe par la stabilité et la répétition d'expériences sécurisantes",
      "L'expression symbolique (jeu, dessin, parole) et la qualité du lien"
    ]
  },
  {
    id: 88, type: 'content',
    title: "Lien DC1 – Prévention et Accompagnement des situations complexes",
    content: "L'approche de Cyrulnik soutient une posture de prévention précoce. La prévention ne consiste pas seulement à repérer le risque, mais aussi à renforcer les facteurs protecteurs. L'environnement d'accueil devient un espace de réparation symbolique.",
    bullets: [
      "Observer les signes de souffrance, repérer les difficultés relationnelles",
      "Dans des contextes de séparation parentale, précarité, conflits familiaux ou expériences traumatiques",
      "Sécuriser, valoriser les compétences, soutenir l'estime de soi, favoriser l'expression émotionnelle"
    ]
  },
  {
    id: 89, type: 'content',
    title: "Exemple concret et Mobiliser Cyrulnik dans le Livret 2",
    content: "Exemple : Un enfant présente des comportements d'opposition suite à un changement familial. Le professionnel analyse le contexte, propose des temps de jeu symbolique, maintient une présence stable et valorise les réussites. Cette posture s'inscrit dans une logique de soutien à la résilience.",
    bullets: [
      "En référence à Cyrulnik, je considère que la relation éducative peut constituer un facteur de protection pour l'enfant confronté à des situations complexes",
      "Mon accompagnement vise à renforcer les facteurs protecteurs et à soutenir les capacités de reconstruction de l'enfant"
    ]
  },
  // GUEDENEY
  {
    id: 90, type: 'pedagogue',
    name: "Antoine Guedeney",
    dates: "Contemporain",
    citation: "Le retrait relationnel est un signal d'alerte.",
    portrait: "https://customer-assets.emergentagent.com/job_7ea634f4-6c60-43bd-ad87-41da18485b19/artifacts/nh2vy62p_image.png",
    description: "Antoine Guedeney est pédopsychiatre et spécialiste du développement précoce et des troubles du lien. Ses travaux portent principalement sur le repérage des signes précoces de souffrance relationnelle chez le nourrisson et le jeune enfant.",
    description2: "Il s'inscrit dans une approche clinique rigoureuse visant à identifier les indicateurs précoces de retrait relationnel afin de favoriser une intervention rapide et adaptée.",
    dc: "DC1"
  },
  {
    id: 91, type: 'content',
    title: "1. Le retrait relationnel précoce",
    content: "Le retrait relationnel précoce correspond à une diminution inhabituelle de l'engagement de l'enfant dans la relation. Il n'est pas un simple tempérament calme. Il peut être un indicateur de souffrance psychique ou de difficulté dans la relation d'attachement.",
    bullets: [
      "Une réduction du regard et une faible expressivité faciale",
      "Une diminution des vocalises et une faible réactivité aux stimulations",
      "Une posture corporelle peu engagée",
      "Il constitue un signal d'alerte nécessitant une observation attentive"
    ]
  },
  {
    id: 92, type: 'content',
    title: "2. L'échelle ADBB (Alarm Distress Baby Scale)",
    content: "Antoine Guedeney a développé l'ADBB, outil d'évaluation clinique permettant de mesurer le degré de retrait relationnel chez le jeune enfant. Il ne s'agit pas de poser un diagnostic, mais d'identifier des éléments nécessitant une vigilance accrue.",
    bullets: [
      "Contact visuel, expression faciale, activité corporelle",
      "Réactivité à la stimulation, capacité d'engagement relationnel",
      "Utilisée dans un cadre médical ou spécialisé",
      "La connaissance de ses critères permet aux professionnels de repérer des signaux préoccupants"
    ]
  },
  {
    id: 93, type: 'content',
    title: "Lien DC1 – Repérage et Prévention précoce",
    content: "Les travaux de Guedeney soutiennent la compétence de repérage précoce des difficultés relationnelles. Plus l'intervention est précoce, plus les possibilités d'évolution favorable sont importantes.",
    bullets: [
      "Observer les interactions quotidiennes, noter les variations d'engagement",
      "Échanger en équipe pour prendre du recul",
      "Observation régulière, formalisation des écrits, partenariat avec les services de santé",
      "Posture bienveillante, non stigmatisante, respectueuse du cadre réglementaire"
    ]
  },
  {
    id: 94, type: 'content',
    title: "Exemple concret et Mobiliser Guedeney dans le Livret 2",
    content: "Exemple : Un enfant habituellement expressif devient moins souriant, peu réactif aux sollicitations, en retrait lors des temps collectifs. Le professionnel observe sur plusieurs jours, partage en équipe, échange avec la famille et oriente si nécessaire vers la PMI.",
    bullets: [
      "En m'appuyant sur les travaux de Guedeney, je porte une attention particulière aux signes discrets de désengagement relationnel chez le jeune enfant",
      "Mon observation régulière me permet de repérer d'éventuels signaux d'alerte et d'inscrire mon action dans une démarche de prévention précoce"
    ]
  },
  // ═══════════════════════════════════════
  // SECTION 6 : FONDEMENTS PSYCHANALYTIQUES
  // ═══════════════════════════════════════
  { id: 95, type: 'section', title: "Fondements psychanalytiques du développement" },
  // FREUD
  {
    id: 96, type: 'pedagogue',
    name: "Sigmund Freud",
    dates: "1856–1939",
    citation: "Les premières expériences relationnelles structurent la personnalité.",
    portrait: "https://customer-assets.emergentagent.com/job_7ea634f4-6c60-43bd-ad87-41da18485b19/artifacts/m2gvj1av_image.png",
    description: "Sigmund Freud est neurologue et fondateur de la psychanalyse. Ses travaux ont profondément influencé la compréhension du développement affectif et de la construction psychique de l'enfant.",
    description2: "Bien que ses théories soient issues d'un contexte historique spécifique, elles ont posé les bases d'une réflexion majeure sur l'influence des premières expériences relationnelles dans la structuration de la personnalité. Freud introduit l'idée que le développement psychique s'organise autour de stades successifs liés aux zones érogènes et aux modalités relationnelles.",
    dc: "DC1"
  },
  {
    id: 97, type: 'content',
    title: "1. La théorie des stades psychosexuels",
    content: "Freud décrit plusieurs stades du développement psychique. Dans le cadre de la petite enfance, les stades oral et anal sont particulièrement pertinents.",
    bullets: [
      "Stade oral (0-1 an) : plaisir centré sur la bouche, le nourrisson découvre le monde à travers l'oralité",
      "Les expériences de satisfaction ou de frustration participent à la construction du sentiment de sécurité",
      "Stade anal (1-3 ans) : le contrôle sphinctérien devient central, l'enfant expérimente la maîtrise et l'autonomie",
      "Les attitudes éducatives autour de la propreté influencent la relation à la règle et à l'autorité",
      "Stade phallique (3-6 ans) : l'enfant s'interroge sur les différences sexuées et construit les bases de son identité"
    ]
  },
  {
    id: 98, type: 'content',
    title: "2. La construction psychique",
    content: "Freud introduit l'idée que la personnalité se construit progressivement à partir des premières expériences relationnelles. Les premières interactions avec les figures parentales influencent l'équilibre entre les trois instances. Les expériences précoces ont une portée structurante.",
    bullets: [
      "Le Ça : pulsions et désirs",
      "Le Moi : instance médiatrice",
      "Le Surmoi : intériorisation des interdits et normes"
    ]
  },
  {
    id: 99, type: 'content',
    title: "Lien DC1 – Compréhension des besoins affectifs et Posture éducative",
    content: "La théorie freudienne rappelle que les expériences corporelles participent à la construction psychique, les soins précoces influencent la sécurité intérieure et les frustrations doivent être progressives et adaptées.",
    bullets: [
      "Les temps de repas, de change et d'apprentissage de la propreté sont porteurs d'enjeux affectifs importants",
      "Comprendre les enjeux du stade anal permet d'éviter une pression excessive ou une rigidité éducative",
      "Le respect du rythme de l'enfant contribue à une structuration psychique plus harmonieuse"
    ]
  },
  {
    id: 100, type: 'content',
    title: "Exemple concret et Mobiliser Freud dans le Livret 2",
    content: "Exemple : Lors de l'apprentissage de la propreté, le professionnel respecte le rythme, évite les injonctions, valorise les progrès et accueille les accidents sans jugement. Cette posture prend en compte les enjeux affectifs liés au contrôle et à l'autonomie.",
    bullets: [
      "Les apports de Freud m'amènent à considérer les temps de soins comme des moments structurants dans la construction affective de l'enfant",
      "Je veille à respecter le rythme de l'enfant dans les acquisitions liées à la propreté, en tenant compte des enjeux affectifs associés au stade anal"
    ]
  },
  // ═══════════════════════════════════════
  // SECTION 7 : COMMUNICATION ET DYNAMIQUE
  // ═══════════════════════════════════════
  { id: 101, type: 'section', title: "Communication et dynamique de groupe" },
  // LEWIN
  {
    id: 102, type: 'pedagogue',
    name: "Kurt Lewin",
    dates: "1890–1947",
    citation: "Le groupe est plus que la somme de ses membres.",
    portrait: "https://customer-assets.emergentagent.com/job_7ea634f4-6c60-43bd-ad87-41da18485b19/artifacts/syf3y7dn_image.png",
    description: "Kurt Lewin est un psychologue germano-américain considéré comme le fondateur de la psychologie sociale moderne et de la théorie de la dynamique de groupe. Il introduit une approche scientifique des interactions humaines.",
    description2: "Sa célèbre formule résume son idée : B = f(P,E) — Le comportement est fonction de la Personne et de son Environnement. Ses travaux ont profondément influencé la compréhension des phénomènes collectifs, du leadership et des processus de changement.",
    dc: "DC3/DC4"
  },
  {
    id: 103, type: 'content',
    title: "1. La dynamique de groupe",
    content: "Lewin démontre qu'un groupe ne se réduit pas à la somme des individus qui le composent. Comprendre la dynamique de groupe permet d'agir sur le climat relationnel plutôt que de se centrer uniquement sur un comportement individuel.",
    bullets: [
      "Des rôles implicites et explicites, des normes collectives",
      "Des interactions spécifiques et des phénomènes d'influence",
      "En structure petite enfance : interactions entre enfants, sous-groupes, phénomènes d'imitation, tensions collectives"
    ]
  },
  {
    id: 104, type: 'content',
    title: "2. Les styles de leadership",
    content: "Lewin identifie trois styles principaux de leadership. Ses recherches montrent que le style démocratique favorise la coopération, l'engagement, la créativité et la stabilité émotionnelle.",
    bullets: [
      "Autoritaire : décisions centralisées, peu de participation",
      "Démocratique : participation active du groupe",
      "Laisser-faire : faible encadrement",
      "Dans le champ éducatif, cela invite à privilégier une posture structurante mais participative"
    ]
  },
  {
    id: 105, type: 'content',
    title: "3. Les processus collectifs et le changement",
    content: "Lewin est à l'origine d'un modèle du changement en trois étapes. Ce modèle s'applique particulièrement au travail d'équipe et à l'évolution des pratiques professionnelles.",
    bullets: [
      "1. Décristallisation (prise de conscience)",
      "2. Changement",
      "3. Recristallisation (stabilisation)",
      "Toute transformation nécessite une réflexion collective, une mobilisation du groupe et une régulation continue"
    ]
  },
  {
    id: 106, type: 'content',
    title: "Lien pratique – Animation de groupe et Travail d'équipe",
    content: "La dynamique de groupe permet de comprendre les tensions entre enfants, d'ajuster la posture d'animation et de favoriser la coopération. Dans l'équipe pluriprofessionnelle, elle permet d'identifier les rôles informels et de prévenir les conflits.",
    bullets: [
      "L'éducateur pose un cadre clair, régule les interactions, encourage la participation",
      "Il agit sur l'organisation du groupe et non uniquement sur les comportements isolés",
      "Le style de leadership adopté influence directement le climat d'équipe et la qualité du projet éducatif"
    ]
  },
  {
    id: 107, type: 'content',
    title: "Exemple concret et Mobiliser Lewin dans le Livret 2",
    content: "Exemple : Lors d'un temps collectif, un petit groupe monopolise l'attention et exclut un enfant plus discret. Le professionnel observe la dynamique, rééquilibre les prises de parole, valorise la participation de chacun et propose une activité favorisant la coopération.",
    bullets: [
      "En m'appuyant sur Lewin, j'analyse les interactions collectives afin d'ajuster mon animation et de favoriser un climat coopératif",
      "La compréhension des processus collectifs me permet d'adopter un leadership démocratique favorisant la participation et l'implication de chacun"
    ]
  },
  // ROSENBERG
  {
    id: 108, type: 'pedagogue',
    name: "Marshall Rosenberg",
    dates: "1934–2015",
    citation: "Les mots peuvent être des fenêtres ou des murs.",
    portrait: "https://customer-assets.emergentagent.com/job_7ea634f4-6c60-43bd-ad87-41da18485b19/artifacts/npoozhlw_image.png",
    description: "Marshall Rosenberg est psychologue américain et fondateur de la Communication Non Violente (CNV). Son approche vise à transformer les conflits en opportunités de compréhension mutuelle en mettant l'accent sur les besoins fondamentaux plutôt que sur les jugements ou les accusations.",
    description2: "La citation « Les mots peuvent être des fenêtres ou des murs » illustre l'idée que le langage peut soit ouvrir à la relation, soit créer de la distance.",
    dc: "DC1/DC3"
  },
  {
    id: 109, type: 'content',
    title: "Les quatre étapes de la CNV",
    content: "La Communication Non Violente repose sur quatre étapes structurantes permettant de désamorcer les tensions, d'éviter les interprétations et de favoriser l'écoute mutuelle et la coopération.",
    bullets: [
      "1. Observation : décrire les faits sans jugement",
      "2. Sentiment : exprimer l'émotion ressentie",
      "3. Besoin : identifier le besoin sous-jacent",
      "4. Demande : formuler une demande claire et réalisable"
    ]
  },
  {
    id: 110, type: 'content',
    title: "Lien pratique – Médiation entre enfants et Relation avec les familles",
    content: "Exemple : « Tu as pris le camion des mains de Paul (observation). Il pleure et semble en colère (sentiment). Il avait besoin de continuer son jeu (besoin). Peux-tu lui demander s'il est d'accord pour échanger ? (demande). » Cette reformulation évite la culpabilisation et n'étiquette pas l'enfant.",
    bullets: [
      "La CNV favorise une posture respectueuse, non accusatoire, clarifiant les attentes",
      "Particulièrement pertinente dans les situations sensibles : désaccord éducatif, inquiétude parentale, tension relationnelle"
    ]
  },
  {
    id: 111, type: 'content',
    title: "Mobiliser Rosenberg dans le Livret 2",
    content: "J'utilise les principes de la Communication Non Violente développés par Marshall Rosenberg afin de favoriser une médiation respectueuse lors des conflits. Cette approche me permet de distinguer les faits des jugements, d'identifier les besoins des enfants et de formuler des demandes claires favorisant la coopération.",
    bullets: [
      "En m'appuyant sur la CNV, je veille à verbaliser les situations de manière factuelle et à accompagner l'expression des émotions des enfants",
      "Cette posture favorise la régulation des tensions et soutient l'apprentissage des compétences sociales"
    ]
  },
  // ROGERS
  {
    id: 112, type: 'pedagogue',
    name: "Carl Rogers",
    dates: "1902–1987",
    citation: "Être empathique, c'est voir le monde à travers les yeux de l'autre.",
    portrait: "https://customer-assets.emergentagent.com/job_7ea634f4-6c60-43bd-ad87-41da18485b19/artifacts/hqyj5011_image.png",
    description: "Carl Rogers est un psychologue américain, fondateur de l'approche humaniste et de la thérapie centrée sur la personne. Ses travaux ont profondément influencé les pratiques éducatives, sociales et thérapeutiques.",
    description2: "Rogers place la qualité de la relation au cœur du développement humain. Un climat relationnel sécurisant et authentique permet à la personne de mobiliser ses ressources internes. Son approche repose sur trois attitudes fondamentales : l'empathie, la congruence et le regard positif inconditionnel.",
    dc: "DC1/DC3"
  },
  {
    id: 113, type: 'content',
    title: "1. L'empathie",
    content: "L'empathie consiste à comprendre le monde intérieur de l'autre comme si l'on était à sa place, sans perdre la conscience qu'il s'agit de l'expérience de l'autre. Il ne s'agit pas de sympathie ni d'identification. L'empathie favorise la confiance et l'ouverture.",
    bullets: [
      "Une écoute active",
      "Une reformulation",
      "Une attention aux émotions",
      "Une validation des ressentis"
    ]
  },
  {
    id: 114, type: 'content',
    title: "2. La congruence et 3. Le non-jugement",
    content: "La congruence correspond à l'authenticité du professionnel : une cohérence entre ce que l'on pense, ce que l'on ressent et ce que l'on exprime. Le regard positif inconditionnel consiste à accueillir la personne sans jugement moral.",
    bullets: [
      "Un professionnel congruent adopte une posture sincère et claire, sans masque ni rigidité",
      "Respecter la singularité, éviter les interprétations hâtives",
      "Accueillir les difficultés sans stigmatiser",
      "Dans le champ de la petite enfance, cette posture est essentielle dans la relation aux familles"
    ]
  },
  {
    id: 115, type: 'content',
    title: "Lien DC3/DC1 – Relation aux familles et Communication professionnelle",
    content: "L'approche rogérienne soutient une communication fondée sur l'écoute, la reconnaissance des compétences parentales et le respect des choix éducatifs. La posture rogérienne favorise la gestion apaisée des désaccords et la régulation des tensions en équipe.",
    bullets: [
      "Reformuler les propos, accueillir les inquiétudes, éviter toute posture culpabilisante",
      "Cela contribue à instaurer un climat de confiance",
      "L'empathie et la congruence renforcent la qualité des échanges professionnels"
    ]
  },
  {
    id: 116, type: 'content',
    title: "Exemple concret et Mobiliser Rogers dans le Livret 2",
    content: "Exemple : Un parent exprime son inquiétude. Le professionnel écoute sans interrompre, reformule « Si je comprends bien, vous êtes inquiet parce que... », valorise « Vous êtes attentif à son évolution » et apporte des éléments objectifs.",
    bullets: [
      "En m'appuyant sur Rogers, j'adopte une posture d'écoute empathique favorisant la confiance et la coopération avec les familles",
      "Je veille à maintenir une communication professionnelle congruente et respectueuse, permettant un échange constructif avec les parents et l'équipe"
    ]
  },
  // ═══════════════════════════════════════
  // SECTION 8 : TRAVAIL SOCIAL ET PETITE ENFANCE
  // ═══════════════════════════════════════
  { id: 117, type: 'section', title: "Travail social et petite enfance" },
  // MYRIAM DAVID
  {
    id: 118, type: 'pedagogue',
    name: "Myriam David",
    dates: "1917–2004",
    citation: "L'enfant a besoin d'un adulte repère identifié.",
    portrait: "https://customer-assets.emergentagent.com/job_7ea634f4-6c60-43bd-ad87-41da18485b19/artifacts/zk6v4uie_image.png",
    description: "Myriam David est pédopsychiatre française, spécialisée dans le développement du jeune enfant et les effets des séparations précoces. Ses travaux, menés notamment auprès d'enfants placés, ont profondément influencé les pratiques en collectivité petite enfance.",
    description2: "Elle s'est particulièrement intéressée à la qualité des liens précoces, à la continuité relationnelle et à l'organisation des structures d'accueil pour garantir la sécurité affective des enfants. Son approche articule clinique, observation fine et organisation institutionnelle.",
    dc: "DC1"
  },
  {
    id: 119, type: 'content',
    title: "1. Le système de référence",
    content: "Myriam David développe le concept de système de référence en collectivité. L'idée centrale est qu'un enfant accueilli en structure a besoin d'un adulte repère identifié, d'une continuité relationnelle et d'une stabilité affective. Cette organisation évite la dilution des responsabilités affectives dans le collectif.",
    bullets: [
      "Le référent assure la continuité des soins",
      "Il suit l'évolution de l'enfant",
      "Il est interlocuteur privilégié de la famille",
      "Il sécurise les temps de séparation"
    ]
  },
  {
    id: 120, type: 'content',
    title: "2. La sécurité affective en collectivité",
    content: "Myriam David démontre que la vie en collectivité peut être sécurisante si certaines conditions sont réunies. La collectivité ne doit pas devenir un espace anonyme. La qualité des soins et la régularité des interactions contribuent à la construction de la sécurité intérieure.",
    bullets: [
      "Stabilité des professionnels et organisation cohérente",
      "Individualisation des soins et respect du rythme",
      "Observation attentive",
      "L'enfant a besoin de repères clairs dans un environnement structuré"
    ]
  },
  {
    id: 121, type: 'content',
    title: "Implications pratiques – Continuité, Individualisation et Familles",
    content: "Le système de référence permet une meilleure adaptation, une réduction de l'angoisse de séparation et un accompagnement personnalisé. Les soins sont réalisés prioritairement par le référent lorsque possible.",
    bullets: [
      "Cette continuité renforce le sentiment de sécurité et favorise l'attachement secondaire",
      "Le référent devient interlocuteur privilégié, médiateur et garant de la cohérence éducative",
      "Cela facilite la confiance et la communication avec les familles"
    ]
  },
  {
    id: 122, type: 'content',
    title: "Lien DC1 et Mobiliser David dans le Livret 2",
    content: "Les travaux de Myriam David soutiennent directement les compétences liées à la relation privilégiée, la sécurité affective, l'accompagnement individualisé, la qualité des soins et l'accueil des familles. Ils renforcent l'importance d'une organisation institutionnelle pensée pour le bien-être psychique de l'enfant.",
    bullets: [
      "En m'appuyant sur les travaux de Myriam David, je veille à assurer une continuité relationnelle permettant à l'enfant de bénéficier d'un repère stable au sein du collectif",
      "La mise en place d'un référent participe à la sécurité affective de l'enfant et facilite son adaptation à la vie en collectivité"
    ]
  },
  // APPELL
  {
    id: 123, type: 'pedagogue',
    name: "Geneviève Appell",
    dates: "1924–2025",
    citation: "L'observation institutionnelle est un outil central de professionnalisation.",
    portrait: "https://customer-assets.emergentagent.com/job_7ea634f4-6c60-43bd-ad87-41da18485b19/artifacts/hbzy8etw_image.png",
    description: "Geneviève Appell est psychologue française, collaboratrice de Myriam David, connue pour ses travaux sur le développement du jeune enfant en collectivité et sur l'importance de l'observation institutionnelle.",
    description2: "Ses recherches ont notamment porté sur les effets de la séparation précoce et sur l'organisation des structures d'accueil afin de garantir une sécurité affective suffisante. Elle contribue à articuler approche clinique, observation rigoureuse et organisation éducative.",
    dc: "DC1"
  },
  {
    id: 124, type: 'content',
    title: "1. L'observation institutionnelle",
    content: "Geneviève Appell met en avant l'importance d'une observation structurée et partagée au sein des institutions d'accueil. Elle constitue un outil central de professionnalisation.",
    bullets: [
      "L'observation doit être régulière, écrite, analysée en équipe et inscrite dans une réflexion collective",
      "Elle ne doit pas être informelle, subjective ou isolée",
      "Elle permet de repérer les besoins singuliers et d'ajuster les pratiques",
      "Elle permet de prévenir les difficultés relationnelles et d'assurer une cohérence éducative"
    ]
  },
  {
    id: 125, type: 'content',
    title: "2. La référence éducative",
    content: "En complément des travaux de Myriam David, Geneviève Appell approfondit la notion de référence éducative. La référence ne se limite pas à un adulte identifié. Elle contribue à éviter l'anonymat relationnel en collectivité.",
    bullets: [
      "Une responsabilité éducative claire",
      "Un suivi individualisé et une connaissance fine de l'enfant",
      "Un lien privilégié avec la famille",
      "La référence garantit continuité relationnelle, stabilité affective et meilleure compréhension des comportements"
    ]
  },
  {
    id: 126, type: 'content',
    title: "Implications pratiques – Prévention, Cohérence et Individualisation",
    content: "L'observation institutionnelle permet de repérer des signes de retrait, d'identifier des changements comportementaux et de soutenir les familles. Le partage des observations évite les interprétations individuelles et renforce la cohérence éducative.",
    bullets: [
      "L'observation favorise une posture réflexive et analytique",
      "La référence éducative permet d'adapter les soins, de respecter le rythme et de personnaliser l'accompagnement",
      "Elle renforce la sécurité affective au sein du groupe"
    ]
  },
  {
    id: 127, type: 'content',
    title: "Lien DC1 et Mobiliser Appell dans le Livret 2",
    content: "Les travaux de Geneviève Appell soutiennent directement la compétence d'observation, l'accompagnement individualisé, la relation privilégiée, la prévention précoce et la cohérence institutionnelle.",
    bullets: [
      "En m'appuyant sur les travaux de Geneviève Appell relatifs à l'observation institutionnelle, je formalise et partage mes observations en équipe afin d'ajuster les réponses éducatives aux besoins de l'enfant",
      "La mise en place d'une référence éducative me permet d'assurer une continuité relationnelle et un accompagnement individualisé au sein du collectif"
    ]
  },
  // ═══════════════════════════════════════
  // SECTION 9 : OBSERVATION ET ANALYSE
  // ═══════════════════════════════════════
  { id: 128, type: 'section', title: "Observation et analyse éducative" },
  // FONTAINE
  {
    id: 129, type: 'pedagogue',
    name: "Anne-Marie Fontaine",
    dates: "Contemporaine",
    citation: "L'observation est un levier de transformation éducative.",
    portrait: "https://customer-assets.emergentagent.com/job_7ea634f4-6c60-43bd-ad87-41da18485b19/artifacts/4aowl7r5_image.png",
    description: "Anne-Marie Fontaine est chercheuse et spécialiste de la petite enfance. Ses travaux portent sur l'observation professionnelle en structure d'accueil et sur l'analyse contextualisée des situations éducatives.",
    description2: "Elle contribue à faire évoluer l'observation d'une simple prise de notes vers un véritable outil d'analyse et de projet. Son approche insiste sur la nécessité de donner du sens aux observations pour qu'elles orientent concrètement l'action éducative.",
    dc: "DC1/DC2"
  },
  {
    id: 130, type: 'content',
    title: "1. L'observation-projet",
    content: "Anne-Marie Fontaine développe la notion d'observation-projet. L'observation ne consiste pas uniquement à décrire un comportement. Elle devient un levier de transformation, permettant de passer du constat à l'action éducative réfléchie.",
    bullets: [
      "Identifier une question professionnelle",
      "Recueillir des données factuelles",
      "Analyser les interactions",
      "Ajuster les pratiques",
      "Exemple : observer les interactions dans un coin jeu, identifier une difficulté, modifier l'aménagement, évaluer l'impact"
    ]
  },
  {
    id: 131, type: 'content',
    title: "2. L'analyse éducative contextualisée",
    content: "Fontaine insiste sur l'importance de contextualiser les situations éducatives. Un comportement ne peut être compris isolément. Cette analyse évite les interprétations hâtives ou stigmatisantes et favorise une posture professionnelle réflexive et nuancée.",
    bullets: [
      "Le moment de la journée",
      "L'environnement spatial",
      "La dynamique de groupe",
      "L'histoire de l'enfant",
      "Le climat institutionnel"
    ]
  },
  {
    id: 132, type: 'content',
    title: "Implications pratiques – Professionnalisation et Ajustement",
    content: "L'approche de Fontaine invite à structurer les observations, distinguer faits et interprétations, partager les analyses en équipe et inscrire l'observation dans un projet éducatif. Elle renforce la légitimité professionnelle.",
    bullets: [
      "Adapter les activités et modifier l'aménagement",
      "Repenser les modalités d'intervention",
      "Évaluer l'efficacité des actions mises en place",
      "Soutenir une démarche d'amélioration continue"
    ]
  },
  {
    id: 133, type: 'content',
    title: "Lien DC1/DC2 et Mobiliser Fontaine dans le Livret 2",
    content: "Les travaux d'Anne-Marie Fontaine soutiennent la compétence d'observation, la conception de situations éducatives adaptées, l'analyse des pratiques et l'évaluation des actions éducatives. Ils renforcent la dimension réflexive du métier d'EJE.",
    bullets: [
      "En m'appuyant sur la notion d'observation-projet développée par Anne-Marie Fontaine, je structure mes observations afin d'ajuster mes propositions éducatives en fonction du contexte et des besoins repérés",
      "L'analyse contextualisée me permet de comprendre les comportements des enfants au regard de leur environnement et d'éviter les interprétations hâtives"
    ]
  },
  // FÉLICITATIONS
  {
    id: 134, type: 'congrats',
    title: "Félicitations !",
    message: "Vous avez terminé la présentation des principaux pédagogues du DEEJE. Vous disposez désormais des repères théoriques essentiels pour enrichir votre Livret 2 et ancrer votre pratique professionnelle dans des références solides.",
    cta: "Poursuivez avec les cours DC1 à DC4 pour approfondir chaque domaine de compétences"
  },
];

const DEEJEDC4PedagoguesPresentation = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [imgError, setImgError] = useState({});
  const synthRef = useRef(window.speechSynthesis);

  const slide = slides[currentSlide];
  const total = slides.length;

  const goNext = () => { if (currentSlide < total - 1) setCurrentSlide(currentSlide + 1); stopSpeech(); };
  const goPrev = () => { if (currentSlide > 0) setCurrentSlide(currentSlide - 1); stopSpeech(); };

  const stopSpeech = () => { synthRef.current.cancel(); setIsSpeaking(false); };
  const toggleSpeech = () => {
    if (isSpeaking) { stopSpeech(); return; }
    const text = slide.description || slide.content || slide.message || slide.points?.join('. ') || slide.bullets?.join('. ') || '';
    if (!text) return;
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = 'fr-FR';
    utterance.onend = () => setIsSpeaking(false);
    synthRef.current.speak(utterance);
    setIsSpeaking(true);
  };

  const progress = ((currentSlide + 1) / total) * 100;

  const renderSlide = () => {
    switch (slide.type) {
      case 'title':
        return (
          <div data-testid="ped-slide-title" style={{ textAlign: 'center', padding: '3rem 2rem' }}>
            {slide.image && <img src={slide.image} alt="" style={{ width: '100%', maxHeight: '250px', objectFit: 'cover', borderRadius: '1rem', marginBottom: '2rem' }} />}
            <h1 style={{ fontSize: '2rem', fontWeight: '700', color: '#7C3AED', marginBottom: '1rem' }}>{slide.title}</h1>
            <p style={{ fontSize: '1.1rem', color: '#666' }}>{slide.subtitle}</p>
          </div>
        );
      case 'section':
        return (
          <div data-testid="ped-slide-section" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '300px', background: 'linear-gradient(135deg, #7C3AED11, #7C3AED22)', borderRadius: '1rem', padding: '3rem' }}>
            <h2 style={{ fontSize: '1.8rem', fontWeight: '700', color: '#7C3AED', textAlign: 'center' }}>{slide.title}</h2>
          </div>
        );
      case 'pedagogue':
        const showAvatar = !slide.portrait || imgError[slide.id];
        return (
          <div data-testid="ped-slide-pedagogue" style={{ padding: '2rem' }}>
            <div style={{ display: 'flex', gap: '2rem', alignItems: 'flex-start', flexWrap: 'wrap' }}>
              <div style={{ flexShrink: 0, width: '160px', height: '200px', borderRadius: '1rem', border: '3px solid #7C3AED40', overflow: 'hidden', background: 'linear-gradient(135deg, #7C3AED 0%, #9F67FF 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {slide.portrait && !imgError[slide.id] ? (
                  <img src={slide.portrait} alt={slide.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={() => setImgError(prev => ({ ...prev, [slide.id]: true }))} />
                ) : (
                  <span style={{ fontSize: '3rem', fontWeight: '700', color: 'white' }}>{slide.name.split(' ').map(n => n[0]).join('')}</span>
                )}
              </div>
              <div style={{ flex: 1, minWidth: '250px' }}>
                <h2 style={{ fontSize: '1.5rem', fontWeight: '700', color: '#7C3AED', marginBottom: '0.25rem' }}>{slide.name}</h2>
                <span style={{ fontSize: '0.85rem', color: '#999', display: 'block', marginBottom: '0.75rem' }}>{slide.dates} — Lien : {slide.dc}</span>
                {slide.citation && (
                  <blockquote style={{ borderLeft: '3px solid #7C3AED', paddingLeft: '1rem', fontStyle: 'italic', color: '#555', marginBottom: '1rem', fontSize: '1.05rem' }}>
                    {slide.citation}
                  </blockquote>
                )}
                <p style={{ lineHeight: '1.7', color: '#444' }}>{slide.description}</p>
                {slide.description2 && <p style={{ lineHeight: '1.7', color: '#444', marginTop: '0.75rem' }}>{slide.description2}</p>}
              </div>
            </div>
          </div>
        );
      case 'content':
        return (
          <div data-testid="ped-slide-content" style={{ padding: '2rem' }}>
            <h3 style={{ fontSize: '1.3rem', fontWeight: '700', color: '#7C3AED', marginBottom: '1.25rem' }}>{slide.title}</h3>
            {slide.bullets && (
              <ul style={{ listStyle: 'none', padding: 0, marginBottom: '1.5rem' }}>
                {slide.bullets.map((b, i) => (
                  <li key={i} style={{ padding: '0.5rem 0', paddingLeft: '1.5rem', position: 'relative', lineHeight: '1.6', color: '#444', borderBottom: '1px solid #f0f0f0' }}>
                    <span style={{ position: 'absolute', left: 0, color: '#7C3AED', fontWeight: '700' }}>&#x25B8;</span>
                    {b}
                  </li>
                ))}
              </ul>
            )}
            {slide.content && (
              <div style={{ background: '#7C3AED08', border: '1px solid #7C3AED20', borderRadius: '0.75rem', padding: '1rem 1.25rem' }}>
                <p style={{ fontSize: '0.9rem', color: '#555', fontStyle: 'italic', lineHeight: '1.6' }}>{slide.content}</p>
              </div>
            )}
          </div>
        );
      case 'summary':
        return (
          <div data-testid="ped-slide-summary" style={{ padding: '2rem' }}>
            <h3 style={{ fontSize: '1.3rem', fontWeight: '700', color: '#7C3AED', marginBottom: '1.25rem' }}>{slide.title}</h3>
            <ol style={{ paddingLeft: '1.25rem' }}>
              {slide.points.map((p, i) => (
                <li key={i} style={{ padding: '0.4rem 0', lineHeight: '1.6', color: '#444' }}>{p}</li>
              ))}
            </ol>
          </div>
        );
      case 'congrats':
        return (
          <div data-testid="ped-slide-congrats" style={{ textAlign: 'center', padding: '3rem 2rem' }}>
            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>&#127891;</div>
            <h2 style={{ fontSize: '1.8rem', fontWeight: '700', color: '#7C3AED', marginBottom: '1rem' }}>{slide.title}</h2>
            <p style={{ lineHeight: '1.7', color: '#555', maxWidth: '600px', margin: '0 auto 1.5rem' }}>{slide.message}</p>
            <p style={{ color: '#7C3AED', fontWeight: '600' }}>{slide.cta}</p>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div style={{ maxWidth: '900px', margin: '0 auto' }}>
      {/* Barre de progression en pourcentage */}
      <div style={{ width: '100%', height: '8px', background: '#e5e7eb', borderRadius: '4px', marginBottom: '0.5rem' }}>
        <div style={{ width: `${progress}%`, height: '100%', background: 'linear-gradient(90deg, #7C3AED, #9F67FF)', borderRadius: '4px', transition: 'width 0.3s' }} />
      </div>

      {/* Slide */}
      <div className="card" style={{ minHeight: '400px', margin: 0 }}>
        {renderSlide()}
      </div>

      {/* Contrôles en bas avec numéro de slide */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.75rem 0', marginTop: '0.5rem', borderTop: '1px solid #eee' }}>
        <button data-testid="ped-prev-btn" onClick={goPrev} disabled={currentSlide === 0}
          style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.6rem 1.25rem', border: '1px solid #7C3AED', borderRadius: '0.5rem', background: 'white', color: '#7C3AED', cursor: currentSlide === 0 ? 'not-allowed' : 'pointer', opacity: currentSlide === 0 ? 0.4 : 1, fontWeight: '500', fontSize: '0.9rem' }}>
          <ChevronLeft size={16} /> Précédent
        </button>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <button onClick={toggleSpeech}
            style={{ width: '36px', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center', border: 'none', borderRadius: '50%', background: isSpeaking ? '#7C3AED' : '#7C3AED', color: 'white', cursor: 'pointer' }}>
            {isSpeaking ? <VolumeX size={16} /> : <Volume2 size={16} />}
          </button>
          <span style={{ fontWeight: '700', fontSize: '0.9rem', color: '#7C3AED' }}>{currentSlide + 1} / {total}</span>
          <span style={{ fontSize: '0.8rem', color: '#999' }}>{Math.round(progress)}%</span>
        </div>
        <button data-testid="ped-next-btn" onClick={goNext} disabled={currentSlide === total - 1}
          style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.6rem 1.25rem', border: 'none', borderRadius: '0.5rem', background: '#7C3AED', color: 'white', cursor: currentSlide === total - 1 ? 'not-allowed' : 'pointer', opacity: currentSlide === total - 1 ? 0.4 : 1, fontWeight: '500', fontSize: '0.9rem' }}>
          Suivant <ChevronRight size={16} />
        </button>
      </div>
    </div>
  );
};

export default DEEJEDC4PedagoguesPresentation;
